import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";

import BASE_URL from "../../../config";
import { WarningBackendApi } from "../Base UI Elements/SweetAlert";

// Signup lives on its own page (/signup), separate from the login pages, because login has several
// methods (SMS OTP, WhatsApp, email + password, Google) and mixing signup in makes them confusing.
//
// Gmail-first signup: Google (verifies the email) -> Lender/Borrower -> mobile OTP -> the existing
// register form pre-filled -> account created. Anyone who prefers the classic route goes to /userType.

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 4px 32px rgba(0,0,0,0.10)",
  padding: "40px 36px",
  maxWidth: 420,
  width: "100%",
  margin: "auto",
};

const btnPrimary = {
  display: "block",
  width: "100%",
  padding: "13px 0",
  background: "#1a73e8",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  marginTop: 4,
  letterSpacing: 0.2,
};

const btnOutline = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  padding: "11px 0",
  background: "#fff",
  border: "1.5px solid #dadce0",
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  color: "#3c4043",
  cursor: "pointer",
  marginBottom: 10,
};

const inputStyle = {
  width: "100%",
  padding: "13px 14px",
  border: "1.5px solid #dadce0",
  borderRadius: 8,
  fontSize: 16,
  outline: "none",
  marginTop: 6,
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const dividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  margin: "20px 0",
  color: "#aaa",
  fontSize: 13,
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.7 33.1 30.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l6-6C34.5 6.5 29.6 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c11 0 20.5-8 20.5-19.5 0-1.3-.1-2.7-.5-4z"/>
    <path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.2 13 24 13c3.1 0 5.8 1.1 8 2.9l6-6C34.5 6.5 29.6 4.5 24 4.5c-7.7 0-14.3 4.4-17.7 10.2z"/>
    <path fill="#FBBC05" d="M24 43.5c5.8 0 10.8-1.9 14.5-5.2l-6.7-5.5C29.8 34.7 27 35.5 24 35.5c-6 0-10.7-3.9-11.7-9.1l-7 5.4C8.5 39.5 15.7 43.5 24 43.5z"/>
    <path fill="#EA4335" d="M44.5 20H24v8.5h11.7c-.6 2.7-2.2 4.9-4.4 6.4l6.7 5.5C41.8 36.7 44.5 30.8 44.5 24c0-1.3-.1-2.7-.5-4z"/>
  </svg>
);

const Signup = () => {
  const history = useNavigate();

  // === Stages: "entry" | "already" | "gmail_role" | "gmail_register"
  const [stage, setStage] = useState("entry");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [googleEmail, setGoogleEmail] = useState("");
  const [googleName, setGoogleName] = useState("");
  const [grRole, setGrRole] = useState(null); // "LENDER" | "BORROWER"
  const [grMobile, setGrMobile] = useState("");
  const [grMobileError, setGrMobileError] = useState("");
  const [grOtpSent, setGrOtpSent] = useState(false);
  const [grOtp, setGrOtp] = useState("");
  const [grOtpError, setGrOtpError] = useState("");

  // Google popup -> is this Google account already on OxyLoans?
  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/v1/user/checkGoogleEmail`,
        { accessToken: tokenResponse.access_token },
        { headers: { "Content-Type": "application/json" } }
      );
      const { phoneNumberRequiredOrNot: status, signInUrl } = res.data;

      if (status === "NOT_FOUND") {
        // New user - pick role first, then verify mobile
        setGoogleToken(tokenResponse.access_token);
        setGoogleEmail(signInUrl || "");
        setGoogleName(res.data.googleName || "");
        setStage("gmail_role");
      } else if (status === "LINKED" || status === "FOUND" || status === "STEP2_PENDING") {
        // Already registered - signup is not the place to log in; point them to login
        setGoogleEmail(signInUrl || "");
        setStage("already");
      } else {
        WarningBackendApi("Google Sign-Up Failed", "Unexpected response. Please try again.");
      }
    } catch (e) {
      const msg = e?.response?.data?.errorMessage || "Google sign-up failed. Please try again.";
      WarningBackendApi("Google Sign-Up Failed", msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleSignup = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => WarningBackendApi("Google Sign-Up Failed", "Authentication was cancelled or failed."),
  });

  const handleBack = () => {
    setStage("entry");
    setGoogleToken(null);
    setGoogleEmail("");
    setGoogleName("");
    setGrRole(null);
    sessionStorage.removeItem("gmail_prefill");
    setGrMobile("");
    setGrMobileError("");
    setGrOtpSent(false);
    setGrOtp("");
    setGrOtpError("");
  };

    // Gmail-first registration handlers
  const handleGmailRegSendOtp = async () => {
    if (!/^\d{10}$/.test(grMobile)) {
      setGrMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/v1/user/sendGoogleRegMobileOtp`, { mobileNumber: grMobile });
      const status = res.data?.phoneNumberRequiredOrNot;
      if (status === "OTP_SENT") {
        setGrOtpSent(true);
      } else if (status === "ALREADY_REGISTERED") {
        setGrMobileError("This mobile number is already registered. Please login instead.");
      } else {
        setGrMobileError("Could not send OTP. Please try again.");
      }
    } catch (e) {
      setGrMobileError(e?.response?.data?.errorMessage || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGmailRegVerifyOtp = async () => {
    if (grOtp.trim().length < 4) {
      setGrOtpError("Please enter the OTP.");
      return;
    }
    setLoading(true);
    try {
      // Verify OTP by calling the check endpoint
      const verifyRes = await axios.post(`${BASE_URL}/v1/user/verifyGoogleRegMobileOtp`, {
        mobileNumber: grMobile,
        mobileOtp: grOtp,
        googleAccessToken: googleToken, // server validates it with Google and derives the email itself
      });
      if (!verifyRes.data?.valid) {
        setGrOtpError("Invalid OTP. Please try again.");
        return;
      }
      // Store pre-fill for the registration form
      sessionStorage.setItem("gmail_prefill", JSON.stringify({
        email: googleEmail,
        mobile: grMobile,
        name: googleName,
        emailVerified: true,
        role: grRole,
      }));
      // Redirect to the appropriate registration form for full details + KYC
      if (grRole === "BORROWER") {
        history("/borrower_register");
      } else {
        history("/register");
      }
    } catch (e) {
      setGrOtpError(e?.response?.data?.errorMessage || "Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#1a1a2e", letterSpacing: -0.5 }}>
          🏦 OxyLoans
        </div>
        <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
          RBI-Registered P2P Lending Platform
        </div>
      </div>

      <div style={cardStyle}>

        {/* ── STAGE: entry ── */}
        {stage === "entry" && (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1a1a2e", margin: "0 0 4px" }}>
              Create your account
            </h2>
            <p style={{ color: "#666", fontSize: 14, margin: "0 0 24px" }}>
              Start lending and earning returns today.
            </p>

            <button style={btnOutline} onClick={() => googleSignup()} disabled={googleLoading}>
              {googleLoading ? <span className="spinner-border spinner-border-sm" /> : <GoogleIcon />}
              Sign up with Google
            </button>
            <p style={{ fontSize: 12, color: "#888", margin: "-2px 0 0", textAlign: "center" }}>
              Fastest: Google verifies your email, so no activation link.
            </p>

            <div style={dividerStyle}>
              <div style={{ flex: 1, height: 1, background: "#ebebeb" }} />
              or
              <div style={{ flex: 1, height: 1, background: "#ebebeb" }} />
            </div>

            <Link to="/userType" style={{ ...btnOutline, textDecoration: "none" }}>
              Sign up with mobile &amp; email
            </Link>

            <p style={{ textAlign: "center", fontSize: 14, color: "#555", marginTop: 20 }}>
              Already have an account? <Link to="/loginotp" style={{ color: "#1a73e8", fontWeight: 600 }}>Log in</Link>
            </p>
            <p style={{ textAlign: "center", fontSize: 12, color: "#aaa", marginTop: 12, lineHeight: 1.5 }}>
              By continuing, you agree to our{" "}
              <Link to="/termsandconditions" style={{ color: "#1a73e8" }}>Terms</Link>
              {" & "}
              <Link to="/privacy-policy" style={{ color: "#1a73e8" }}>Privacy Policy</Link>
            </p>
          </>
        )}

        {/* ── STAGE: already (Google account is already registered) ── */}
        {stage === "already" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>👋</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e", margin: "0 0 8px" }}>
                You're already registered
              </h2>
              <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                {googleEmail ? <><strong>{googleEmail}</strong> already has</> : "This Google account already has"} an OxyLoans account.
                Please log in instead.
              </p>
            </div>
            <Link to="/loginotp" style={{ ...btnPrimary, textDecoration: "none", textAlign: "center" }}>
              Go to login →
            </Link>
            <button
              onClick={handleBack}
              style={{ display: "block", width: "100%", marginTop: 12, background: "none", border: "none", color: "#1a73e8", fontSize: 14, cursor: "pointer" }}
            >
              ← Use a different account
            </button>
          </>
        )}

        {/* ── STAGE: gmail_role (new user — choose Lender or Borrower) ── */}
        {stage === "gmail_role" && (
          <>
            <div style={{ background: "#e8f5e9", border: "1px solid #4caf50", borderRadius: 8, padding: "12px 16px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>✅</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2e7d32" }}>Gmail verified</div>
                <div style={{ fontSize: 12, color: "#555", wordBreak: "break-all" }}>{googleEmail}</div>
              </div>
            </div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: "0 0 6px" }}>
              How do you want to join OxyLoans?
            </h3>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 20px" }}>
              Choose your role to continue registration.
            </p>
            <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
              <button
                onClick={() => { setGrRole("LENDER"); setStage("gmail_register"); }}
                style={{ flex: 1, padding: "18px 12px", border: "2px solid #1a73e8", borderRadius: 10, background: "#f0f6ff", cursor: "pointer", textAlign: "center" }}
              >
                <div style={{ fontSize: 28, marginBottom: 6 }}>💰</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1a73e8" }}>Lender</div>
                <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>Invest &amp; earn returns</div>
              </button>
              <button
                onClick={() => { setGrRole("BORROWER"); setStage("gmail_register"); }}
                style={{ flex: 1, padding: "18px 12px", border: "2px solid #e65100", borderRadius: 10, background: "#fff8f5", cursor: "pointer", textAlign: "center" }}
              >
                <div style={{ fontSize: 28, marginBottom: 6 }}>🏦</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#e65100" }}>Borrower</div>
                <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>Apply for a loan</div>
              </button>
            </div>
            <button
              onClick={handleBack}
              style={{ display: "block", width: "100%", marginTop: 14, background: "none", border: "none", color: "#888", fontSize: 13, cursor: "pointer" }}
            >
              ← Back to sign-up options
            </button>
          </>
        )}

        {/* ── STAGE: gmail_register (role chosen — collect mobile + OTP) ── */}
        {stage === "gmail_register" && (
          <>
            {/* Gmail verified + role badge */}
            <div style={{ background: "#e8f5e9", border: "1px solid #4caf50", borderRadius: 8, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>✅</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2e7d32" }}>
                  Gmail verified — registering as <strong>{grRole === "BORROWER" ? "Borrower" : "Lender"}</strong>
                </div>
                <div style={{ fontSize: 12, color: "#555", wordBreak: "break-all" }}>{googleEmail}</div>
              </div>
            </div>

            {!grOtpSent ? (
              <>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", display: "block", marginBottom: 8 }}>
                  Enter your mobile number to verify
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={grMobile}
                  onChange={e => { setGrMobile(e.target.value.replace(/\D/g, "")); setGrMobileError(""); }}
                  style={{ ...inputStyle, borderColor: grMobileError ? "#d32f2f" : "#dadce0" }}
                  autoFocus
                />
                {grMobileError && <div style={{ color: "#d32f2f", fontSize: 13, marginTop: 6 }}>{grMobileError}</div>}
                <button
                  style={{ ...btnPrimary, marginTop: 16, opacity: loading ? 0.7 : 1 }}
                  disabled={loading}
                  onClick={handleGmailRegSendOtp}
                >
                  {loading ? "Sending OTP…" : "Send OTP →"}
                </button>
                <button
                  onClick={() => { setGrOtpSent(false); setGrMobile(""); setGrMobileError(""); setStage("gmail_role"); }}
                  style={{ display: "block", width: "100%", marginTop: 10, background: "none", border: "none", color: "#888", fontSize: 13, cursor: "pointer" }}
                >
                  ← Change role
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, color: "#555", marginBottom: 12 }}>
                  OTP sent to <strong>{grMobile}</strong>
                </div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", display: "block", marginBottom: 8 }}>
                  Enter OTP
                </label>
                <input
                  type="tel"
                  maxLength={6}
                  placeholder="______"
                  value={grOtp}
                  onChange={e => { setGrOtp(e.target.value.replace(/\D/g, "")); setGrOtpError(""); }}
                  style={{ ...inputStyle, letterSpacing: 8, fontSize: 22, textAlign: "center", borderColor: grOtpError ? "#d32f2f" : "#dadce0" }}
                  autoFocus
                />
                {grOtpError && <div style={{ color: "#d32f2f", fontSize: 13, marginTop: 6 }}>{grOtpError}</div>}
                <button
                  style={{ ...btnPrimary, marginTop: 16, opacity: loading ? 0.7 : 1 }}
                  disabled={loading}
                  onClick={handleGmailRegVerifyOtp}
                >
                  {loading ? "Verifying…" : "Verify & Continue →"}
                </button>
                <button
                  onClick={() => { setGrOtpSent(false); setGrOtp(""); setGrOtpError(""); }}
                  style={{ display: "block", width: "100%", marginTop: 10, background: "none", border: "none", color: "#1a73e8", fontSize: 13, cursor: "pointer" }}
                >
                  Change mobile number
                </button>
              </>
            )}

            <button
              onClick={handleBack}
              style={{ display: "block", width: "100%", marginTop: 14, background: "none", border: "none", color: "#888", fontSize: 13, cursor: "pointer" }}
            >
              ← Back to sign-up options
            </button>
          </>
        )}

      </div>

      {/* Trust badges */}
      <div style={{ display: "flex", gap: 20, marginTop: 24, color: "#888", fontSize: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <span>🔒 SSL Secured</span>
        <span>🏦 RBI Registered NBFC</span>
        <span>🛡️ ₹50L Lending Limit</span>
      </div>
    </div>
  );
};

export default Signup;
